import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Menu, MenuItem } from '@lifeomic/chroma-react/components/Menu';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import clsx from 'clsx';
import {
  registerMarkdownShortcuts,
  UNORDERED_LIST,
  ORDERED_LIST,
} from '@lexical/markdown';
import {
  COMMAND_PRIORITY_LOW,
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
  $createParagraphNode,
  RangeSelection,
  createCommand,
  LexicalCommand,
} from 'lexical';
import {
  $insertList,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListType,
  ListNode,
  $removeList,
} from '@lexical/list';
import { $setBlocksType } from '@lexical/selection';
import {
  $createHeadingNode,
  HeadingNode,
  HeadingTagType,
} from '@lexical/rich-text';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { $isSelectionInTable } from '../utils/tableUtils';
import {
  INSERT_TABLE_COMMAND,
  INSERT_TABLE_ROW_ABOVE_COMMAND,
  INSERT_TABLE_ROW_BELOW_COMMAND,
  INSERT_TABLE_COLUMN_LEFT_COMMAND,
  INSERT_TABLE_COLUMN_RIGHT_COMMAND,
  DELETE_TABLE_ROW_COMMAND,
  DELETE_TABLE_COLUMN_COMMAND,
} from './TableManipulationPlugin';
import {
  RotateCcw,
  RotateCw,
  Bold,
  Italic,
  Underline,
  NumberList,
  List,
  Strikethrough,
  Plus,
  Minus,
  Grid,
  ChevronDown,
} from '@lifeomic/chromicons';
import { defineMessages, useIntl } from 'react-intl';

export const FORMAT_HEADING_COMMAND: LexicalCommand<HeadingTagType | null> =
  createCommand();

const HEADING_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5'] as const;

const useStyles = makeStyles(theme => ({
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 8,
    '& > svg': {
      margin: 0,
    },
  },
  divider: {
    position: 'absolute',
    left: -24,
    top: 58,
    width: `calc(100% + 48px)`,
    height: 4,
    opacity: 0,
    transition: 'opacity 0.3s ease-in-out',
    '&.visible': {
      opacity: 1,
    },
  },
  dividerVertical: {
    width: 1,
    marginTop: 4,
    '&&': {
      height: 28,
    },
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

const $getNearestHeading = (
  selection: RangeSelection,
  tag?: HeadingTagType,
) => {
  const heading = $getNearestNodeOfType(selection.getNodes()[0], HeadingNode);
  if (tag) {
    if (heading && heading.getTag() === tag) {
      return heading;
    }
    return null;
  }
  return heading;
};

const $getNearestHeadingTagType = (
  selection: RangeSelection,
): HeadingTagType | null => {
  const heading = $getNearestHeading(selection);
  return heading ? heading.getTag() : null;
};

const $getNearestListType = (selection: RangeSelection): ListType | null => {
  const list = $getNearestNodeOfType(selection.getNodes()[0], ListNode);
  return list ? list.getListType() : null;
};

type ToolbarPluginProps = {
  className?: string;
  isEditing?: boolean;
};

const messages = defineMessages({
  insertTable: {
    id: 'ToolbarPlugin.insertTable',
    defaultMessage: 'Insert Table',
  },
  tableOptions: {
    id: 'ToolbarPlugin.tableOptions',
    defaultMessage: 'Table Options',
  },
  insertRowAbove: {
    id: 'ToolbarPlugin.insertRowAbove',
    defaultMessage: 'Insert Row Above',
  },
  insertRowBelow: {
    id: 'ToolbarPlugin.insertRowBelow',
    defaultMessage: 'Insert Row Below',
  },
  insertColumnLeft: {
    id: 'ToolbarPlugin.insertColumnLeft',
    defaultMessage: 'Insert Column Left',
  },
  insertColumnRight: {
    id: 'ToolbarPlugin.insertColumnRight',
    defaultMessage: 'Insert Column Right',
  },
  deleteRow: {
    id: 'ToolbarPlugin.deleteRow',
    defaultMessage: 'Delete Row',
  },
  deleteColumn: {
    id: 'ToolbarPlugin.deleteColumn',
    defaultMessage: 'Delete Column',
  },
  insertUnorderedList: {
    id: 'ToolbarPlugin.insertUnorderedList',
    defaultMessage: 'Format Unordered List',
  },
  insertOrderedList: {
    id: 'ToolbarPlugin.insertOrderedList',
    defaultMessage: 'Format Ordered List',
  },
  formatBold: {
    id: 'ToolbarPlugin.formatBold',
    defaultMessage: 'Format Bold',
  },
  formatItalic: {
    id: 'ToolbarPlugin.formatItalic',
    defaultMessage: 'Format Italics',
  },
});

export const ToolbarPlugin = ({
  className,
  isEditing = false,
}: ToolbarPluginProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [headingTag, setHeadingTag] = useState<HeadingTagType | null>(null);
  const [listType, setListType] = useState<ListType | null>(null);
  const [isAtTop, setIsAtTop] = useState(true); // Start as true so divider is hidden initially
  const [isInTable, setIsInTable] = useState(false);

  useEffect(() => {
    return registerMarkdownShortcuts(editor, [UNORDERED_LIST, ORDERED_LIST]);
  }, [editor]);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setHeadingTag($getNearestHeadingTagType(selection));
      setListType($getNearestListType(selection));
      setIsInTable($isSelectionInTable());
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        payload => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        payload => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        FORMAT_HEADING_COMMAND,
        headingTag => {
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            const headingTagType = $getNearestHeadingTagType(selection);
            if (headingTagType === headingTag) {
              $setBlocksType(selection, () => $createParagraphNode());
            } else if (headingTag !== null) {
              $setBlocksType(selection, () => $createHeadingNode(headingTag));
            }
          }
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        INSERT_UNORDERED_LIST_COMMAND,
        () => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return false;
          }
          const listType = $getNearestListType(selection);
          if (listType === 'bullet') {
            $removeList();
          } else {
            $insertList('bullet');
          }
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        INSERT_ORDERED_LIST_COMMAND,
        () => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return false;
          }
          const listType = $getNearestListType(selection);
          if (listType === 'number') {
            $removeList();
          } else {
            $insertList('number');
          }
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, $updateToolbar]);

  useEffect(() => {
    if (!isEditing) {
      setCanUndo(false);
      setCanRedo(false);
      setIsBold(false);
      setIsItalic(false);
      setIsUnderline(false);
      setIsStrikethrough(false);
      setIsInTable(false);
    }
  }, [isEditing]);

  // Monitor scroll position to detect when toolbar is within 250px of es-details top
  useEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar || !isEditing) {
      return;
    }

    // Find the es-details container
    const esDetailsContainer = document.querySelector(
      '.es-details',
    ) as HTMLElement;
    if (!esDetailsContainer) {
      return;
    }

    const checkPosition = () => {
      // Use scroll position to determine if we're within 250px of the top
      const scrollTop = esDetailsContainer.scrollTop;
      const isWithin250px = scrollTop <= 250;

      setIsAtTop(isWithin250px);
    };

    // Check position initially
    checkPosition();

    // Listen for scroll events on the es-details container
    esDetailsContainer.addEventListener('scroll', checkPosition);
    // Also listen for resize events in case layout changes
    window.addEventListener('resize', checkPosition);

    return () => {
      esDetailsContainer.removeEventListener('scroll', checkPosition);
      window.removeEventListener('resize', checkPosition);
    };
  }, [isEditing]);

  // Reset scroll position when editing starts
  useEffect(() => {
    if (isEditing) {
      // When editing starts from the context (edit button clicked)
      const esDetailsContainer = document.querySelector('.es-details');
      if (esDetailsContainer) {
        esDetailsContainer.scrollTop = 0;
        // Set isAtTop to true since we're at the top
        setIsAtTop(true);
      }
    }
  }, [isEditing]);

  if (!isEditing) {
    return null;
  }

  return (
    <>
      <Box
        direction="row"
        fullWidth
        ref={toolbarRef}
        className={className}
        gap={0.5}
      >
        <Box direction="row" gap={1}>
          <Button
            disabled={!canUndo}
            onClick={() => {
              editor.dispatchCommand(UNDO_COMMAND, undefined);
            }}
            className={classes.button}
            aria-label="Undo"
            variant="outlined"
            icon={RotateCcw}
          />
          <Button
            disabled={!canRedo}
            onClick={() => {
              editor.dispatchCommand(REDO_COMMAND, undefined);
            }}
            className={classes.button}
            aria-label="Redo"
            variant="outlined"
            icon={RotateCw}
          />
        </Box>
        <Divider direction="row" className={classes.dividerVertical} />
        <Box direction="row" gap={1}>
          {HEADING_TAGS.map(tag => (
            <Button
              key={tag}
              onClick={() => {
                editor.dispatchCommand(FORMAT_HEADING_COMMAND, tag);
              }}
              variant={headingTag === tag ? 'contained' : 'outlined'}
              aria-label={`Format ${tag}`}
              className={classes.button}
            >
              <span>{tag}</span>
            </Button>
          ))}
          <Button
            onClick={() => {
              editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
            }}
            variant={listType === 'bullet' ? 'contained' : 'outlined'}
            aria-label={formatMessage(messages.insertUnorderedList)}
            className={classes.button}
            icon={List}
          />
          <Button
            onClick={() => {
              editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
            }}
            variant={listType === 'number' ? 'contained' : 'outlined'}
            aria-label={formatMessage(messages.insertOrderedList)}
            className={classes.button}
            icon={NumberList}
          />
          <Button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
            }}
            variant={isBold ? 'contained' : 'outlined'}
            aria-label={formatMessage(messages.formatBold)}
            className={classes.button}
            icon={Bold}
          />
          <Button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
            }}
            variant={isItalic ? 'contained' : 'outlined'}
            aria-label={formatMessage(messages.formatItalic)}
            className={classes.button}
            icon={Italic}
          />
          <Button
            onClick={() => {
              if (isStrikethrough) {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
              }
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
            }}
            variant={isUnderline ? 'contained' : 'outlined'}
            aria-label="Format Underline"
            className={classes.button}
            icon={Underline}
          />
          <Button
            onClick={() => {
              if (isUnderline) {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
              }
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
            }}
            variant={isStrikethrough ? 'contained' : 'outlined'}
            aria-label="Format Strikethrough"
            className={classes.button}
            icon={Strikethrough}
          />
        </Box>

        {/* Table controls - menu that shows different options based on cursor position */}
        <Divider direction="row" className={classes.dividerVertical} />
        <Box direction="row" gap={1}>
          <Menu
            placement="bottom-start"
            aria-label={
              isInTable
                ? formatMessage(messages.tableOptions)
                : formatMessage(messages.insertTable)
            }
            anchorElement={
              <Button
                variant="outlined"
                aria-label={formatMessage(messages.tableOptions)}
                className={classes.button}
                icon={Grid}
                trailingIcon={ChevronDown}
                title={
                  isInTable
                    ? formatMessage(messages.tableOptions)
                    : formatMessage(messages.insertTable)
                }
              />
            }
            items={
              isInTable
                ? [
                    // Table manipulation options when cursor is in a table
                    <MenuItem
                      key="insert-row-above"
                      className={classes.menuItem}
                      onClick={() => {
                        editor.dispatchCommand(
                          INSERT_TABLE_ROW_ABOVE_COMMAND,
                          undefined,
                        );
                      }}
                    >
                      <Plus />
                      {formatMessage(messages.insertRowAbove)}
                    </MenuItem>,
                    <MenuItem
                      key="insert-row-below"
                      className={classes.menuItem}
                      onClick={() => {
                        editor.dispatchCommand(
                          INSERT_TABLE_ROW_BELOW_COMMAND,
                          undefined,
                        );
                      }}
                    >
                      <Plus />
                      {formatMessage(messages.insertRowBelow)}
                    </MenuItem>,
                    <MenuItem
                      key="insert-column-left"
                      className={classes.menuItem}
                      onClick={() => {
                        editor.dispatchCommand(
                          INSERT_TABLE_COLUMN_LEFT_COMMAND,
                          undefined,
                        );
                      }}
                    >
                      <Plus />
                      {formatMessage(messages.insertColumnLeft)}
                    </MenuItem>,
                    <MenuItem
                      key="insert-column-right"
                      className={classes.menuItem}
                      onClick={() => {
                        editor.dispatchCommand(
                          INSERT_TABLE_COLUMN_RIGHT_COMMAND,
                          undefined,
                        );
                      }}
                    >
                      <Plus />
                      {formatMessage(messages.insertColumnRight)}
                    </MenuItem>,
                    <Divider key="divider" />,
                    <MenuItem
                      key="delete-row"
                      className={classes.menuItem}
                      onClick={() => {
                        editor.dispatchCommand(
                          DELETE_TABLE_ROW_COMMAND,
                          undefined,
                        );
                      }}
                    >
                      <Minus />
                      {formatMessage(messages.deleteRow)}
                    </MenuItem>,
                    <MenuItem
                      key="delete-column"
                      className={classes.menuItem}
                      onClick={() => {
                        editor.dispatchCommand(
                          DELETE_TABLE_COLUMN_COMMAND,
                          undefined,
                        );
                      }}
                    >
                      <Minus />
                      {formatMessage(messages.deleteColumn)}
                    </MenuItem>,
                  ]
                : [
                    // Table creation option when cursor is not in a table
                    <MenuItem
                      key="insert-table"
                      className={classes.menuItem}
                      onClick={() => {
                        editor.dispatchCommand(INSERT_TABLE_COMMAND, {
                          rows: 2,
                          columns: 2,
                        });
                      }}
                    >
                      <Grid />
                      {formatMessage(messages.insertTable)}
                    </MenuItem>,
                  ]
            }
          />
        </Box>

        <Divider className={clsx(classes.divider, { visible: !isAtTop })} />
      </Box>
    </>
  );
};
